import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { blogAPI } from '../services/api';
import { Plus, Edit2, Trash2, Eye, Save, ArrowLeft, Image, X } from 'lucide-react';
import Button from '../components/UI/Button';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const MAX_IMAGE_WIDTH = 1200;
const IMAGE_QUALITY = 0.75;

/**
 * Resize an image file on a canvas and return a compressed base64 data URL.
 */
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_IMAGE_WIDTH) {
          height = Math.round(height * (MAX_IMAGE_WIDTH / width));
          width = MAX_IMAGE_WIDTH;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', IMAGE_QUALITY));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'indent',
  'blockquote', 'code-block',
  'link', 'image', 'video',
];

const BlogEditor = ({ blog, onSave, onCancel, saving }) => {
  const [title, setTitle] = useState(blog?.title || '');
  const [subject, setSubject] = useState(blog?.subject || '');
  const [content, setContent] = useState(blog?.content || '');
  const [excerpt, setExcerpt] = useState(blog?.excerpt || '');
  const [status, setStatus] = useState(blog?.status || 'draft');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(blog?.thumbnail?.url || '');
  const quillRef = useRef(null);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, false] }],
        [{ font: [] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        image: () => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.click();
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
              const dataUrl = await compressImage(file);
              const editor = quillRef.current?.getEditor();
              if (!editor) return;
              const range = editor.getSelection(true);
              editor.insertEmbed(range.index, 'image', dataUrl);
              editor.setSelection(range.index + 1);
            } catch {
              toast.error('Failed to process image');
            }
          };
        },
      },
    },
  }), []);

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview('');
  };

  const handleSubmit = (submitStatus) => {
    if (!title.trim() || !subject.trim() || !content.trim()) {
      toast.error('Title, subject, and content are required');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('subject', subject);
    formData.append('content', content);
    formData.append('excerpt', excerpt);
    formData.append('status', submitStatus);
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }

    onSave(formData, blog?._id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-ag-black-300 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to posts</span>
        </button>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleSubmit('draft')}
            loading={saving}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleSubmit('published')}
            loading={saving}
            disabled={saving}
          >
            <Eye className="w-4 h-4 mr-2" />
            Publish
          </Button>
        </div>
      </div>

      {/* Thumbnail */}
      <div>
        <label className="block text-sm font-medium text-ag-black-300 mb-2">
          Thumbnail
        </label>
        {thumbnailPreview ? (
          <div className="relative w-full max-w-lg">
            <img
              src={thumbnailPreview}
              alt="Thumbnail preview"
              className="w-full h-48 object-cover rounded-lg border border-ag-black-400"
            />
            <button
              onClick={removeThumbnail}
              className="absolute top-2 right-2 bg-black/70 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center w-full max-w-lg h-48 border-2 border-dashed border-ag-black-400 rounded-lg cursor-pointer hover:border-ag-green transition-colors bg-ag-black-800">
            <Image className="w-8 h-8 text-ag-black-300 mb-2" />
            <span className="text-sm text-ag-black-300">Click to upload thumbnail</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-ag-black-300 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter blog title..."
          className="w-full px-4 py-3 bg-ag-black-800 border border-ag-black-400 rounded-lg text-white text-lg placeholder:text-ag-black-300 focus:outline-none focus:border-ag-green transition-colors"
        />
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-ag-black-300 mb-2">
          Subject / Category
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Tutorial, News, Update..."
          className="w-full px-4 py-3 bg-ag-black-800 border border-ag-black-400 rounded-lg text-white placeholder:text-ag-black-300 focus:outline-none focus:border-ag-green transition-colors"
        />
      </div>

      {/* Excerpt */}
      <div>
        <label className="block text-sm font-medium text-ag-black-300 mb-2">
          Excerpt <span className="text-ag-black-400">(optional — auto-generated if empty)</span>
        </label>
        <textarea
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Short summary of the post..."
          rows={2}
          className="w-full px-4 py-3 bg-ag-black-800 border border-ag-black-400 rounded-lg text-white placeholder:text-ag-black-300 focus:outline-none focus:border-ag-green transition-colors resize-none"
        />
      </div>

      {/* Content — Rich Text Editor */}
      <div>
        <label className="block text-sm font-medium text-ag-black-300 mb-2">
          Content
        </label>
        <div className="quill-dark-wrapper">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={setContent}
            modules={quillModules}
            formats={quillFormats}
            placeholder="Write your blog content here..."
          />
        </div>
      </div>
    </div>
  );
};

const BlogCard = ({ blog, onEdit, onDelete }) => {
  const isPublished = blog.status === 'published';

  return (
    <div className="bg-ag-black-800 border border-ag-black-400 rounded-lg overflow-hidden hover:border-ag-green/50 transition-all duration-300 group">
      {/* Thumbnail */}
      <div className="h-40 bg-ag-black-700 overflow-hidden">
        {blog.thumbnail?.url ? (
          <img
            src={blog.thumbnail.url}
            alt={blog.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-10 h-10 text-ag-black-400" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span
            className={`text-xs font-semibold px-2 py-1 rounded-full ${
              isPublished
                ? 'bg-ag-green/20 text-ag-green'
                : 'bg-ag-black-400/30 text-ag-black-300'
            }`}
          >
            {isPublished ? 'Published' : 'Draft'}
          </span>
          <span className="text-xs text-ag-black-300">
            {new Date(blog.updatedAt).toLocaleDateString()}
          </span>
        </div>

        <h3 className="text-white font-semibold text-sm line-clamp-2 leading-snug">
          {blog.title}
        </h3>

        <p className="text-ag-black-300 text-xs uppercase tracking-wide">
          {blog.subject}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-ag-black-600">
          <button
            onClick={() => onEdit(blog)}
            className="flex items-center gap-1.5 text-xs text-ag-black-300 hover:text-ag-green transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => onDelete(blog._id)}
            className="flex items-center gap-1.5 text-xs text-ag-black-300 hover:text-red-500 transition-colors ml-auto"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageBlogsPage = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await blogAPI.getAll(params);
      setBlogs(res.data.data.blogs);
    } catch (err) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleSave = async (formData, blogId) => {
    try {
      setSaving(true);
      if (blogId) {
        await blogAPI.update(blogId, formData);
        toast.success('Blog updated successfully');
      } else {
        await blogAPI.create(formData);
        toast.success('Blog created successfully');
      }
      setEditingBlog(null);
      setIsCreating(false);
      fetchBlogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await blogAPI.delete(id);
      toast.success('Blog deleted');
      fetchBlogs();
    } catch (err) {
      toast.error('Failed to delete blog');
    }
  };

  // Check authorization
  if (!user || !['admin', 'editor'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-ag-black-900 flex items-center justify-center">
        <p className="text-ag-black-300 text-lg">You do not have permission to access this page.</p>
      </div>
    );
  }

  // Editor view
  if (isCreating || editingBlog) {
    return (
      <div className="min-h-screen bg-ag-black-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BlogEditor
            blog={editingBlog}
            onSave={handleSave}
            onCancel={() => {
              setEditingBlog(null);
              setIsCreating(false);
            }}
            saving={saving}
          />
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-screen bg-ag-black-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Manage Blog Posts</h1>
            <p className="text-ag-black-300 text-sm mt-1">
              Create, edit, and publish blog posts
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={() => setIsCreating(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6">
          {['', 'draft', 'published'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filterStatus === s
                  ? 'bg-ag-green text-black'
                  : 'bg-ag-black-700 text-ag-black-300 hover:text-white'
              }`}
            >
              {s === '' ? 'All' : s === 'draft' ? 'Drafts' : 'Published'}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-12 h-12 text-ag-black-400 mx-auto mb-4" />
            <p className="text-ag-black-300 text-lg">No blog posts yet</p>
            <p className="text-ag-black-400 text-sm mt-1">Click "New Post" to create your first blog post</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {blogs.map((blog) => (
              <BlogCard
                key={blog._id}
                blog={blog}
                onEdit={setEditingBlog}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBlogsPage;
